// src/components/admin/page-management/section-forms/RichTextForm.js
import React, { useState, useEffect, useRef } from 'react';
import ImageUploader from '../ImageUploader';
import VideoUploader from '../VideoUploader'; // Need to create this component
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage, faVideo, faLink, faBold, faItalic, faUnderline, 
  faAlignLeft, faAlignCenter, faAlignRight, faListUl,
  faListOl, faQuoteRight, faHeading, faTable, faPalette
} from '@fortawesome/free-solid-svg-icons';

const RichTextForm = ({ content, updateContent, pageSlug }) => {
  const [htmlContent, setHtmlContent] = useState(content?.richText || '');
  const [editorActive, setEditorActive] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [showVideoOptions, setShowVideoOptions] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageWidth, setImageWidth] = useState('100%');
  const [imageAlignment, setImageAlignment] = useState('center');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [textColor, setTextColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  
  const editorRef = useRef(null);

  // Add debugging for pageSlug
  useEffect(() => {
    console.log('RichTextForm received pageSlug:', pageSlug);
  }, [pageSlug]);

  // Update the parent component when content changes
  useEffect(() => {
    updateContent('richText', htmlContent);
  }, [htmlContent, updateContent]);

  // Helper function to handle commands
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      // Update the HTML content after command execution
      setHtmlContent(editorRef.current.innerHTML);
    }
  };

  // Handler for inserting an image
  const handleInsertImage = () => {
    if (imageUrl) {
      const imgHtml = `<img src="${imageUrl}" alt="${imageAlt}" style="max-width:${imageWidth}; display:block; margin:${
        imageAlignment === 'center' ? '0 auto' : 
        imageAlignment === 'right' ? '0 0 0 auto' : '0 auto 0 0'
      };" />`;
      execCommand('insertHTML', imgHtml);
      setImageUrl('');
      setImageAlt('');
      setShowImageOptions(false);
    }
  };

  // Handler for inserting a video
  const handleInsertVideo = () => {
    if (videoUrl) {
      let videoHtml;
      
      // Check if it's a YouTube URL
      if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
        const videoId = extractYoutubeId(videoUrl);
        videoHtml = `<div class="video-container" style="position:relative; padding-bottom:56.25%; height:0; overflow:hidden; max-width:100%; margin:15px 0;">
          <iframe style="position:absolute; top:0; left:0; width:100%; height:100%;" 
            src="https://www.youtube.com/embed/${videoId}" 
            frameborder="0" allowfullscreen>
          </iframe>
        </div>`;
      } else if (videoUrl.includes('vimeo.com')) {
        // Handle Vimeo videos
        const videoId = extractVimeoId(videoUrl);
        videoHtml = `<div class="video-container" style="position:relative; padding-bottom:56.25%; height:0; overflow:hidden; max-width:100%; margin:15px 0;">
          <iframe style="position:absolute; top:0; left:0; width:100%; height:100%;" 
            src="https://player.vimeo.com/video/${videoId}" 
            frameborder="0" allowfullscreen>
          </iframe>
        </div>`;
      } else {
        // Regular video file
        videoHtml = `<video controls style="max-width:100%; margin:15px 0;">
          <source src="${videoUrl}" type="video/mp4">
          Your browser does not support the video tag.
        </video>`;
      }
      
      execCommand('insertHTML', videoHtml);
      setVideoUrl('');
      setShowVideoOptions(false);
    }
  };

  // Extract YouTube video ID from URL
  const extractYoutubeId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : url;
  };

  // Extract Vimeo video ID from URL
  const extractVimeoId = (url) => {
    const regExp = /vimeo.com\/(\d+)$/;
    const match = url.match(regExp);
    return match ? match[1] : url;
  };

  // Handle the case when an image is uploaded
  const handleImageUploaded = (imagePath) => {
    setImageUrl(imagePath);
  };

  // Handle the case when a video is uploaded
  const handleVideoUploaded = (videoPath) => {
    setVideoUrl(videoPath);
  };

  // Handle content pasted from elsewhere
  const handlePaste = (e) => {
    e.preventDefault();
    
    // Get clipboard data as plain text
    const text = e.clipboardData.getData('text/plain');
    
    // Insert it as plain text to avoid unwanted HTML/formatting
    execCommand('insertText', text);
  };

  // Apply colors to selected text
  const applyColors = () => {
    if (textColor) {
      execCommand('foreColor', textColor);
    }
    if (backgroundColor) {
      execCommand('hiliteColor', backgroundColor);
    }
    setShowColorPicker(false);
  };

  // Explicitly log changes to the rich text content
  const handleContentChange = (html) => {
    console.log('RichText content updated, length:', html.length);
    setHtmlContent(html);
  };

  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Rich Text Section</h3>
      
      {/* Toggle between code and WYSIWYG editor */}
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          className={`mr-2 px-3 py-1 text-sm rounded ${editorActive ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setEditorActive(true)}
        >
          Visual Editor
        </button>
        <button
          type="button"
          className={`px-3 py-1 text-sm rounded ${!editorActive ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setEditorActive(false)}
        >
          HTML Editor
        </button>
        {editorActive && (
          <button
            type="button"
            className={`ml-2 px-3 py-1 text-sm rounded ${previewMode ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? 'Edit Mode' : 'Preview'}
          </button>
        )}
      </div>
      
      {editorActive ? (
        <div className="border rounded overflow-hidden mb-4">
          {/* Toolbar */}
          {!previewMode && (
            <div className="bg-gray-100 p-2 border-b flex flex-wrap gap-1">
              {/* Text formatting */}
              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => execCommand('bold')} title="Bold">
                <FontAwesomeIcon icon={faBold} />
              </button>
              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => execCommand('italic')} title="Italic">
                <FontAwesomeIcon icon={faItalic} />
              </button>
              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => execCommand('underline')} title="Underline">
                <FontAwesomeIcon icon={faUnderline} />
              </button>
              
              <div className="border-r border-gray-300 mx-1"></div>
              
              {/* Alignment */}
              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => execCommand('justifyLeft')} title="Align Left">
                <FontAwesomeIcon icon={faAlignLeft} />
              </button>
              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => execCommand('justifyCenter')} title="Align Center">
                <FontAwesomeIcon icon={faAlignCenter} />
              </button>
              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => execCommand('justifyRight')} title="Align Right">
                <FontAwesomeIcon icon={faAlignRight} />
              </button>
              
              <div className="border-r border-gray-300 mx-1"></div>
              
              {/* Lists */}
              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
                <FontAwesomeIcon icon={faListUl} />
              </button>
              <button className="p-2 hover:bg-gray-200 rounded" onClick={() => execCommand('insertOrderedList')} title="Numbered List">
                <FontAwesomeIcon icon={faListOl} />
              </button>
              
              <div className="border-r border-gray-300 mx-1"></div>
              
              {/* Formatting */}
              <button 
                className="p-2 hover:bg-gray-200 rounded" 
                onClick={() => execCommand('formatBlock', '<h2>')} 
                title="Heading"
              >
                <FontAwesomeIcon icon={faHeading} />
              </button>
              <button 
                className="p-2 hover:bg-gray-200 rounded" 
                onClick={() => execCommand('formatBlock', '<blockquote>')} 
                title="Quote"
              >
                <FontAwesomeIcon icon={faQuoteRight} />
              </button>
              <button 
                className="p-2 hover:bg-gray-200 rounded" 
                onClick={() => execCommand('insertHTML', '<table class="border-collapse border border-gray-300 w-full"><tr><td class="border border-gray-300 p-2">Cell 1</td><td class="border border-gray-300 p-2">Cell 2</td></tr><tr><td class="border border-gray-300 p-2">Cell 3</td><td class="border border-gray-300 p-2">Cell 4</td></tr></table>')} 
                title="Insert Table"
              >
                <FontAwesomeIcon icon={faTable} />
              </button>
              
              <div className="border-r border-gray-300 mx-1"></div>
              
              {/* Media */}
              <button 
                className="p-2 hover:bg-gray-200 rounded" 
                onClick={() => {
                  setShowImageOptions(!showImageOptions);
                  setShowVideoOptions(false);
                  setShowColorPicker(false);
                }} 
                title="Insert Image"
              >
                <FontAwesomeIcon icon={faImage} />
              </button>
              <button 
                className="p-2 hover:bg-gray-200 rounded" 
                onClick={() => {
                  setShowVideoOptions(!showVideoOptions);
                  setShowImageOptions(false);
                  setShowColorPicker(false);
                }} 
                title="Insert Video"
              >
                <FontAwesomeIcon icon={faVideo} />
              </button>
              <button 
                className="p-2 hover:bg-gray-200 rounded" 
                onClick={() => {
                  const url = prompt('Enter URL:');
                  if (url) {
                    const linkText = document.getSelection().toString() || url;
                    execCommand('insertHTML', `<a href="${url}" target="_blank">${linkText}</a>`);
                  }
                }} 
                title="Insert Link"
              >
                <FontAwesomeIcon icon={faLink} />
              </button>
              
              <div className="border-r border-gray-300 mx-1"></div>
              
              {/* Colors */}
              <button 
                className="p-2 hover:bg-gray-200 rounded" 
                onClick={() => {
                  setShowColorPicker(!showColorPicker);
                  setShowImageOptions(false);
                  setShowVideoOptions(false);
                }} 
                title="Text & Background Color"
              >
                <FontAwesomeIcon icon={faPalette} />
              </button>
            </div>
          )}
          
          {/* Image Options */}
          {showImageOptions && !previewMode && (
            <div className="bg-gray-50 p-3 border-b">
              <h4 className="font-medium mb-2">Insert Image</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Image URL</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Alt Text</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    value={imageAlt}
                    onChange={(e) => setImageAlt(e.target.value)}
                    placeholder="Image description"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Width</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={imageWidth}
                    onChange={(e) => setImageWidth(e.target.value)}
                  >
                    <option value="100%">Full width (100%)</option>
                    <option value="75%">Large (75%)</option>
                    <option value="50%">Medium (50%)</option>
                    <option value="25%">Small (25%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Alignment</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={imageAlignment}
                    onChange={(e) => setImageAlignment(e.target.value)}
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Upload Image</label>
                <ImageUploader onImageUpload={handleImageUploaded} pageSlug={pageSlug} />
              </div>
              
              <div className="flex justify-between">
                <button
                  type="button"
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                  onClick={() => setShowImageOptions(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                  onClick={handleInsertImage}
                  disabled={!imageUrl}
                >
                  Insert Image
                </button>
              </div>
            </div>
          )}
          
          {/* Video Options */}
          {showVideoOptions && !previewMode && (
            <div className="bg-gray-50 p-3 border-b">
              <h4 className="font-medium mb-2">Insert Video</h4>
              
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Video URL</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="YouTube, Vimeo or direct video URL"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supports YouTube, Vimeo, or direct MP4 URLs
                </p>
              </div>
              
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Upload Video</label>
                <VideoUploader onVideoUpload={handleVideoUploaded} pageSlug={pageSlug} />
              </div>
              
              <div className="flex justify-between">
                <button
                  type="button"
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                  onClick={() => setShowVideoOptions(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                  onClick={handleInsertVideo}
                  disabled={!videoUrl}
                >
                  Insert Video
                </button>
              </div>
            </div>
          )}
          
          {/* Color Picker */}
          {showColorPicker && !previewMode && (
            <div className="bg-gray-50 p-3 border-b">
              <h4 className="font-medium mb-2">Format Colors</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Text Color</label>
                  <input
                    type="color"
                    className="w-full p-1 border rounded h-10"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Background Color</label>
                  <input
                    type="color"
                    className="w-full p-1 border rounded h-10"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="flex justify-between">
                <button
                  type="button"
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                  onClick={() => setShowColorPicker(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                  onClick={applyColors}
                >
                  Apply Colors
                </button>
              </div>
            </div>
          )}
          
          {/* Content editor */}
          {previewMode ? (
            <div 
              className="p-4 min-h-[300px] rich-text-preview"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            ></div>
          ) : (
            <div 
              ref={editorRef}
              className="p-4 min-h-[300px] focus:outline-none rich-text-editor"
              contentEditable="true"
              onInput={(e) => handleContentChange(e.target.innerHTML)}
              onPaste={handlePaste}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            ></div>
          )}
        </div>
      ) : (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Rich Text Content (HTML)</label>
          <textarea
            className="w-full p-2 border rounded font-mono text-sm"
            rows="15"
            value={htmlContent}
            onChange={(e) => handleContentChange(e.target.value)}
          ></textarea>
          <p className="text-xs text-gray-500 mt-1">
            You can use HTML tags for formatting. For example, &lt;h2&gt;Heading&lt;/h2&gt;, &lt;p&gt;Paragraph&lt;/p&gt;, &lt;strong&gt;Bold&lt;/strong&gt;
          </p>
        </div>
      )}
      
      <style>
        {`
          .rich-text-editor {
            min-height: 300px;
          }
          .rich-text-editor:focus {
            outline: none;
          }
          .rich-text-preview img {
            max-width: 100%;
            height: auto;
          }
          .rich-text-preview table {
            border-collapse: collapse;
            width: 100%;
          }
          .rich-text-preview td, .rich-text-preview th {
            border: 1px solid #ddd;
            padding: 8px;
          }
          .rich-text-preview blockquote {
            border-left: 4px solid #e2e8f0;
            padding-left: 1rem;
            margin-left: 0;
            font-style: italic;
          }
        `}
      </style>
    </div>
  );
};

export default RichTextForm;








// // src/components/admin/page-management/section-forms/RichTextForm.js
// import React from 'react';


// const RichTextForm = ({ content, updateContent }) => {
//   // Add this function to explicitly log what's happening
//   const handleRichTextChange = (e) => {
//     const newText = e.target.value;
//     console.log('Updating rich text to:', newText.substring(0, 100) + '...');
//     updateContent('richText', newText);
//   };

//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Rich Text Section</h3>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium mb-1">Rich Text Content (HTML)</label>
//         <textarea
//           className="w-full p-2 border rounded font-mono text-sm"
//           rows="10"
//           value={content?.richText || ''}
//           onChange={handleRichTextChange}
//         ></textarea>
//         <p className="text-xs text-gray-500 mt-1">
//           You can use HTML tags for formatting. For example, &lt;h2&gt;Heading&lt;/h2&gt;, &lt;p&gt;Paragraph&lt;/p&gt;, &lt;strong&gt;Bold&lt;/strong&gt;
//         </p>
//       </div>
//     </div>
//   );
// };

// export default RichTextForm;